import React from 'react';
import { Text, TextVariant } from '@naf/text';
import { Button, ButtonVariant } from '@naf/button';
import { AdvancedImage, lazyload, placeholder, responsive } from '@cloudinary/react';
import { Link } from 'react-router-dom';
import {
  FeaturedBenefitBlockType,
  FeaturedBenefitStep,
  ColorVariant,
} from '../../../../../../types/featuredBenefitBlockType';
import { useCloudinary } from '../../../../hooks/Cloudinary/useCloudinary';
import { DynamicVideoPlayer } from '../../../video-player/DynamicVideoPlayer';
import { useDocumentUrlWithNode } from '../../../../hooks/useDocumentUrl';
import BlockContent from '../../../block-content/BlockContent';
import { Heading, StepCount, StyledButtonGroup, StyledStep } from './Styles';

export const getButtonStyle = (color: ColorVariant) => {
  const buttonStyle: { primary: ButtonVariant; secondary: ButtonVariant } = {
    primary: 'secondary',
    secondary: 'outline',
  };

  if (color === ColorVariant.signature || color === ColorVariant.outline) {
    buttonStyle.primary = 'signature';
  }

  if (color === ColorVariant.spruce) {
    buttonStyle.primary = 'secondary';
    buttonStyle.secondary = 'outline-inverted';
  }

  if (color === ColorVariant.secondary) {
    buttonStyle.primary = 'spruce';
  }

  return buttonStyle;
};

export const ButtonGroup: React.FC<
  Pick<FeaturedBenefitBlockType, 'colorVariant' | 'primaryCta' | 'secondaryCta'> & { children?: React.ReactNode }
> = ({ primaryCta, secondaryCta, colorVariant, children }) => {
  const primaryInternalItemUrl = useDocumentUrlWithNode(primaryCta?.internalLink);
  const secondaryInternalItemUrl = useDocumentUrlWithNode(secondaryCta?.internalLink);
  if (!primaryCta && !secondaryCta) return null;

  const { primary, secondary } = getButtonStyle(colorVariant);

  return (
    <StyledButtonGroup $breakEarly={!!children}>
      {children}
      {primaryCta && primaryCta.externalLink && primaryCta.externalLink.href && (
        <Button
          href={primaryCta.externalLink.href || ''}
          target={primaryCta.externalLink.blank ? '_blank' : '_self'}
          variant={primary}
        >
          {primaryCta.text}
        </Button>
      )}
      {primaryCta && primaryCta.internalLink && !primaryCta.externalLink?.href && (
        <Link to={primaryInternalItemUrl || ''}>
          <Button type="button" isNotClickable variant={primary}>
            {primaryCta.text}
          </Button>
        </Link>
      )}
      {secondaryCta && secondaryCta.externalLink && secondaryCta.externalLink.href && (
        <Button
          href={secondaryCta.externalLink.href || ''}
          target={secondaryCta.externalLink.blank ? '_blank' : '_self'}
          variant={secondary}
        >
          {secondaryCta.text}
        </Button>
      )}
      {secondaryCta && secondaryCta.internalLink && !secondaryCta.externalLink?.href && (
        <Link to={secondaryInternalItemUrl || ''}>
          <Button isNotClickable type="button" variant={secondary}>
            {secondaryCta.text}
          </Button>
        </Link>
      )}
    </StyledButtonGroup>
  );
};

export const Step: React.FC<{ step: FeaturedBenefitStep; index: number; variant: ColorVariant }> = ({
  variant,
  step,
  index,
}) => (
  <StyledStep>
    <StepCount $variant={variant}>
      <Text variant={TextVariant.Header2}>{index}</Text>
    </StepCount>
    <Heading variant={TextVariant.Header2}>{step.title}</Heading>
    {/* Ensure backwards compatability as we move from string to block content. */}
    {typeof step.description === 'string' ? (
      <Text variant={TextVariant.BodyText}>{step.description}</Text>
    ) : (
      <BlockContent value={step.description} />
    )}
  </StyledStep>
);

export const MediaComponent: React.FC<Pick<FeaturedBenefitBlockType, 'image' | 'video' | 'illustration'>> = ({
  image,
  video,
  illustration,
}) => {
  const { getResponsiveImage } = useCloudinary();

  const cldImage = getResponsiveImage(image?.publicId || image?.public_id, {
    quality: 'auto:good',
    format: illustration ? 'svg' : 'auto',
  });

  if (video) {
    return (
      <DynamicVideoPlayer
        videoPlayerOptions={{ autoplay: true, autoplayMode: 'on-scroll', loop: true, muted: true }}
        video={video}
      />
    );
  }
  if (cldImage) {
    return (
      <AdvancedImage
        style={{ maxWidth: '100%' }}
        alt={image?.altText}
        cldImg={cldImage}
        plugins={[
          lazyload({ rootMargin: '10px 20px 10px 30px', threshold: 0.25 }),
          responsive({ steps: 200 }),
          placeholder({ mode: 'blur' }),
        ]}
      />
    );
  }
  return null;
};
